import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

const UIContext = createContext(null);

export function useUI() {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used within UIProvider');
  return ctx;
}

let _imperativeToast = null;
export function showImperativeToast(message, ms = 5000) {
  console.log('[showImperativeToast]', message, _imperativeToast ? '(bridge connected)' : '(bridge NOT connected — toast will be lost)');
  if (_imperativeToast) _imperativeToast(message, ms);
}

export default function UIProvider({ children }) {
  const [toastMessage, setToastMessage] = useState('');
  const toastTimerRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarPinned, setSidebarPinnedState] = useState(() => {
    try { return localStorage.getItem('maestro_sidebar_pinned') === '1'; } catch { return false; }
  });

  const setSidebarPinned = useCallback((next) => {
    setSidebarPinnedState(next);
    try { localStorage.setItem('maestro_sidebar_pinned', next ? '1' : '0'); } catch {}
    if (next) setSidebarOpen(true);
  }, []);

  const toggleSidebarPinned = useCallback(() => {
    setSidebarPinnedState((prev) => {
      const next = !prev;
      try { localStorage.setItem('maestro_sidebar_pinned', next ? '1' : '0'); } catch {}
      if (next) setSidebarOpen(true);
      return next;
    });
  }, []);

  useEffect(() => {
    if (sidebarPinned) setSidebarOpen(true);
  }, [sidebarPinned]);
  const [showSettings, setShowSettings] = useState(false);
  const [showQRConnect, setShowQRConnect] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showLocalPicker, setShowLocalPicker] = useState(false);
  const [showDevicePicker, setShowDevicePicker] = useState(false);
  const [showQueueSheet, setShowQueueSheet] = useState(false);
  const [showVolumePopover, setShowVolumePopover] = useState(false);
  const [showGenresPanel, setShowGenresPanel] = useState(false);
  const [showPersonalizePanel, setShowPersonalizePanel] = useState(false);
  const [showValetOnboarding, setShowValetOnboarding] = useState(false);
  const [showValetChat, setShowValetChat] = useState(false);
  const [addToPlaylistModal, setAddToPlaylistModal] = useState(null);
  const [signInPrompt, setSignInPrompt] = useState({ isOpen: false, feature: null });

  const [albumOverlay, setAlbumOverlay] = useState(null);

  const [glossaryTerm, setGlossaryTerm] = useState(null);
  const [glossaryPreview, setGlossaryPreview] = useState(null);
  const [entityPeek, setEntityPeek] = useState(null);

  const [devicesVerificationExplainerCollapsed, setDevicesVerificationExplainerCollapsedState] = useState(() => {
    try { return localStorage.getItem('maestro_devices_verification_explainer_collapsed') === '1'; } catch { return false; }
  });

  const setDevicesVerificationExplainerCollapsed = useCallback((collapsed) => {
    setDevicesVerificationExplainerCollapsedState(collapsed);
    try { localStorage.setItem('maestro_devices_verification_explainer_collapsed', collapsed ? '1' : '0'); } catch {}
  }, []);

  const showToast = useCallback((message, ms = 2400) => {
    const msg = String(message || '').trim();
    if (!msg) return;
    setToastMessage(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMessage(''), ms);
  }, []);

  const dismissToast = useCallback(() => {
    setToastMessage('');
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = null;
  }, []);

  useEffect(() => {
    _imperativeToast = showToast;
    return () => { _imperativeToast = null; };
  }, [showToast]);

  const requirePlaybackAuth = useCallback(() => {
    try {
      const stored = localStorage.getItem('maestro_auth');
      const auth = stored ? JSON.parse(stored) : null;
      if (auth?.accessToken) return true;
    } catch {}
    setSignInPrompt({ isOpen: true, feature: 'playback' });
    return false;
  }, []);

  const value = {
    toastMessage,
    showToast,
    dismissToast,
    sidebarOpen, setSidebarOpen,
    sidebarPinned, setSidebarPinned, toggleSidebarPinned,
    showSettings, setShowSettings,
    showQRConnect, setShowQRConnect,
    showAuthModal, setShowAuthModal,
    showTour, setShowTour,
    showLocalPicker, setShowLocalPicker,
    showDevicePicker, setShowDevicePicker,
    showQueueSheet, setShowQueueSheet,
    showVolumePopover, setShowVolumePopover,
    showGenresPanel, setShowGenresPanel,
    showPersonalizePanel, setShowPersonalizePanel,
    showValetOnboarding, setShowValetOnboarding,
    showValetChat, setShowValetChat,
    addToPlaylistModal, setAddToPlaylistModal,
    signInPrompt, setSignInPrompt,
    albumOverlay, setAlbumOverlay,
    glossaryTerm, setGlossaryTerm,
    glossaryPreview, setGlossaryPreview,
    entityPeek, setEntityPeek,
    devicesVerificationExplainerCollapsed, setDevicesVerificationExplainerCollapsed,
    requirePlaybackAuth,
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}
